/**
 * Turning an agent query result into a chart.
 *
 * The report agent settles on a SQL query whose result is `{columns, rows}`.
 * A {@link PanelVizSpec} says how to visualize it (chart type, which column is
 * x, which are series, how to format values). {@link inferVizSpec} guesses a
 * sensible spec from the columns so the builder can preview a chart with zero
 * configuration; {@link resultToDataset} maps result + spec into the shared
 * {@link ChartDataset} that `buildAgOptions` renders. The panel editor (P2c)
 * lets the user override the inferred spec; drilldown (P2d) rides on it too.
 */
import {
  DEFAULT_PALETTE_ORDER,
  resolveToken,
  type ChartDataset,
  type ChartType,
  type CinchColor,
  type DatasetSeries,
  type TrendSpec,
  type ValueFormat,
  makeValueFormatter,
} from "@/components/cinch/charts";

// ── the viz spec (stored on panel.viz; mirrors the plan's JSON shape) ────────

export interface VizSeries {
  key: string;
  name?: string;
  /** A DS token name ("brand") or a concrete color; falls back to the palette. */
  color?: CinchColor | string;
  /** Per-series chart type (combination charts) — overrides the panel's base. */
  type?: "line" | "area" | "bar";
}

export interface VizDrilldown {
  /** In-app Cinch Ops route, e.g. "/contracts". */
  route: string;
  /** Query/param template: value may reference a datum field as "{account_id}". */
  params?: Record<string, string>;
}

/** Curated in-app destinations an author can drill a chart into. Restricting to
 *  a list keeps drill-through targets internal (never off-site). */
export const DRILLDOWN_TARGETS: { route: string; label: string }[] = [
  { route: "/contracts", label: "Contracts" },
  { route: "/customer-tiers", label: "Customer Tiers" },
  { route: "/renewals/dashboard", label: "Renewals" },
  { route: "/parent-companies", label: "Parent Companies" },
  { route: "/proposals", label: "Proposals" },
  { route: "/commissions", label: "Commissions" },
  { route: "/kpi", label: "KPIs" },
  { route: "/glossary", label: "Glossary" },
  { route: "/database", label: "Database Explorer" },
];

/**
 * Resolve a drill-down spec against a clicked datum into an in-app navigation
 * target. Only routes that start with "/" (no scheme/host) are honored, so a
 * click can never leave Cinch Ops. Param templates like "{account_id}" are
 * substituted from the datum; unresolved params are dropped.
 */
export function resolveDrilldown(
  dd: VizDrilldown | undefined,
  datum: Record<string, unknown>,
): { to: string; search: Record<string, string> } | null {
  if (!dd?.route || !dd.route.startsWith("/") || dd.route.startsWith("//")) return null;
  const search: Record<string, string> = {};
  for (const [key, template] of Object.entries(dd.params ?? {})) {
    const value = String(template).replace(/\{([^}]+)\}/g, (_m, col: string) => {
      const v = datum[col.trim()];
      return v == null ? "" : String(v);
    });
    if (value !== "") search[key] = value;
  }
  return { to: dd.route, search };
}

export interface PanelVizSpec {
  chartType?: ChartType;
  xKey?: string;
  series?: VizSeries[];
  valueFormat?: ValueFormat;
  legend?: boolean;
  kind?: "timeseries" | "category";
  /** grid panels: explicit column order (defaults to the result's columns). */
  columns?: string[];
  /** stat panels: which column holds the single value to display. Also the
   *  numeric value column for a split-by pivot. */
  valueKey?: string;
  /** Pivot a category column into one series per distinct value (a "split by" /
   *  series dimension), plotting `valueKey` — turns long-format rows
   *  (x, dimension, value) into a multi-series chart. */
  splitBy?: string;
  /** text panels: the markdown/plain content to render. */
  content?: string;
  drilldown?: VizDrilldown;
  /** optional trend / reference-line overlay (cartesian charts). */
  trend?: TrendSpec;
}

export interface QueryResultLike {
  columns: string[];
  rows: Record<string, unknown>[];
}

// ── dashboard filters: {{param}} placeholders in panel SQL ───────────────────

const PARAM_TOKEN = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

/** Unique `{{name}}` placeholder names across the given SQL strings — the
 *  dashboard renders a filter input per distinct name. */
export function detectParams(sqls: (string | undefined | null)[]): string[] {
  const found = new Set<string>();
  for (const sql of sqls) {
    if (!sql) continue;
    for (const m of sql.matchAll(PARAM_TOKEN)) found.add(m[1]!);
  }
  return [...found];
}

/** Guess a filter input kind from the param name (date-ish → date picker). */
export function guessParamType(name: string): "date" | "text" {
  return /(date|from|to|month|day|start|end|since|until|_at|_on|year|week)/i.test(name)
    ? "date"
    : "text";
}

// ── dashboard filters: client-side auto-filter by result column ──────────────

/** An active column auto-filter: match rows where `row[column] == value`. */
export interface ColumnFilter {
  column: string;
  value: string;
}

/**
 * Apply dashboard column auto-filters to a panel's result *rows*, in the
 * browser. A filter narrows a panel only if that panel's result actually
 * contains the column (the "auto-applies to every panel whose data has that
 * column" model); filters whose column is absent are skipped, so the same
 * filter bar can drive a heterogeneous dashboard. Matching is case-insensitive
 * string equality (categorical columns); empty values are ignored upstream.
 *
 * For a column in the query's GROUP BY, filtering rows post-aggregation is
 * equivalent to adding `AND column = value` before it — so the chart matches
 * what a server-side filter would have produced, while preserving row order and
 * avoiding a refetch. Pre-aggregation range filters use `{{param}}` instead.
 */
export function applyColumnFilters(
  result: QueryResultLike,
  filters: ColumnFilter[] | undefined,
): QueryResultLike {
  const active = (filters ?? []).filter(
    (f) => f.column && f.value !== "" && result.columns.includes(f.column),
  );
  if (active.length === 0) return result;
  const norm = active.map((f) => ({ column: f.column, value: f.value.trim().toLowerCase() }));
  const rows = result.rows.filter((r) =>
    norm.every((f) => String(r[f.column] ?? "").trim().toLowerCase() === f.value),
  );
  return { columns: result.columns, rows };
}

/**
 * Best-effort extraction of a query's top-level output column names (lowercased),
 * for suggesting `column` targets when defining a dashboard auto-filter. Splits
 * the SELECT list on paren-depth-0 commas and takes each item's `AS` alias, or
 * the trailing `table.col` identifier. Function calls without an alias yield no
 * clean name and are skipped — this only powers suggestions, never execution.
 */
export function columnsFromSql(sql: string | undefined | null): string[] {
  if (!sql) return [];
  const m = /\bselect\b([\s\S]*?)\bfrom\b/i.exec(sql);
  if (!m || !m[1]) return [];
  const list = m[1].replace(/^\s*distinct\s+/i, "");
  const items: string[] = [];
  let depth = 0;
  let cur = "";
  for (const ch of list) {
    if (ch === "(") depth += 1;
    else if (ch === ")") depth = Math.max(0, depth - 1);
    if (ch === "," && depth === 0) {
      items.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  if (cur.trim()) items.push(cur);

  const out = new Set<string>();
  for (const raw of items) {
    const item = raw.trim().replace(/\s+/g, " ");
    if (!item || item === "*") continue;
    const asMatch = /\bas\s+"?([A-Za-z_][A-Za-z0-9_]*)"?\s*$/i.exec(item);
    if (asMatch?.[1]) {
      out.add(asMatch[1].toLowerCase());
      continue;
    }
    // No alias: a bare column or `table.col` (skip anything with a call/operator).
    const bare = /^"?([A-Za-z_][A-Za-z0-9_]*)"?(?:\.\s*"?([A-Za-z_][A-Za-z0-9_]*)"?)?$/.exec(item);
    if (bare) out.add((bare[2] ?? bare[1]!).toLowerCase());
  }
  return [...out];
}

/** A query result carrying the SQL that produced it, so a panel saved from it
 *  can re-run the query in the viewer. */
export interface AgentQueryResult extends QueryResultLike {
  sql?: string;
  source?: "snowflake" | "postgres";
}

// ── inference heuristics ─────────────────────────────────────────────────────

const DATE_HINT = /(month|date|day|week|year|period|quarter|_at|_on|time)$|^(month|date|day|week|year|period|quarter)/i;
const USD_HINT = /(arr|mrr|revenue|amount|price|cost|value|usd|booking|spend|total|billed|charge)/i;
const PCT_HINT = /(pct|percent|rate|retention|nrr|gdr|margin|ratio|share)/i;

const CINCH_COLOR_NAMES = new Set<string>(DEFAULT_PALETTE_ORDER);

function toColor(c: CinchColor | string, fallback: string): string {
  if (!c) return fallback;
  return CINCH_COLOR_NAMES.has(c) ? resolveToken(c as CinchColor) : c;
}

/** True if every non-null value in the column parses as a finite number. */
function isNumericColumn(rows: Record<string, unknown>[], col: string): boolean {
  let seen = 0;
  for (const r of rows) {
    const v = r[col];
    if (v == null || v === "") continue;
    seen += 1;
    if (typeof v === "number" && Number.isFinite(v)) continue;
    if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) continue;
    return false;
  }
  return seen > 0;
}

/** Guess a sensible viz spec from a result's shape — the zero-config preview. */
export function inferVizSpec(result: QueryResultLike): PanelVizSpec {
  const cols = result.columns;
  if (cols.length === 0) return {};

  const numeric = cols.filter((c) => isNumericColumn(result.rows, c));
  const nonNumeric = cols.filter((c) => !numeric.includes(c));

  // cols is non-empty (guarded above); the final fallback is always defined.
  const xKey = nonNumeric.find((c) => DATE_HINT.test(c)) ?? nonNumeric[0] ?? cols[0]!;
  const seriesCols = (numeric.length ? numeric : cols).filter((c) => c !== xKey);
  const series: VizSeries[] = (seriesCols.length ? seriesCols : [cols[0]!]).map((c) => ({
    key: c,
  }));

  const dateish = DATE_HINT.test(xKey);
  const kind: "timeseries" | "category" = dateish ? "timeseries" : "category";
  const chartType: ChartType = dateish ? "line" : "bar";

  const names = seriesCols.join(" ");
  const valueFormat: ValueFormat = PCT_HINT.test(names)
    ? "pct"
    : USD_HINT.test(names)
      ? "usd_compact"
      : "count";

  return { chartType, xKey, series, valueFormat, kind, legend: series.length > 1 };
}

// ── result → chart-ready dataset ─────────────────────────────────────────────

/**
 * Pivot long-format rows (x, dimension, value) into a wide multi-series dataset:
 * one series per distinct `splitBy` value, `valueKey` summed per (x, value). This
 * is what makes a `GROUP BY month, type` query render as a stacked/grouped chart
 * split by type — the reports viz otherwise maps columns→series and can't pivot.
 */
function pivotDataset(
  result: QueryResultLike,
  spec: PanelVizSpec,
  xKey: string,
): ChartDataset {
  const dim = spec.splitBy!;
  const valKey = spec.valueKey!;
  const palette = DEFAULT_PALETTE_ORDER.map(resolveToken);

  const xOrder: string[] = [];
  const splitOrder: string[] = [];
  const seenX = new Set<string>();
  const seenS = new Set<string>();
  for (const r of result.rows) {
    const x = String(r[xKey] ?? "");
    const s = String(r[dim] ?? "");
    if (!seenX.has(x)) (seenX.add(x), xOrder.push(x));
    if (!seenS.has(s)) (seenS.add(s), splitOrder.push(s));
  }

  const byX = new Map<string, Record<string, string | number | null>>();
  for (const x of xOrder) {
    const row: Record<string, string | number | null> = { [xKey]: x };
    for (const s of splitOrder) row[s] = null;
    byX.set(x, row);
  }
  for (const r of result.rows) {
    const row = byX.get(String(r[xKey] ?? ""));
    if (!row) continue;
    const s = String(r[dim] ?? "");
    const v = r[valKey];
    const n = v == null || v === "" ? null : Number(v);
    if (n != null && Number.isFinite(n)) row[s] = ((row[s] as number | null) ?? 0) + n;
  }

  return {
    key: "panel",
    label: "Preview",
    kind: spec.kind ?? "timeseries",
    chartTypes: ["stacked-bar", "bar", "line", "area", "stacked-area"],
    categoryKey: xKey,
    rows: xOrder.map((x) => byX.get(x)!),
    series: splitOrder.map((s) => ({ key: s, name: s })),
    colors: splitOrder.map((_, i) => palette[i % palette.length]!),
    valueFormat: makeValueFormatter(spec.valueFormat ?? "count"),
    ...(spec.trend ? { trend: spec.trend } : {}),
  };
}

/** Map a query result + viz spec into the shared ChartDataset. Inference fills
 *  any gap the spec leaves, so a bare `{}` spec still renders. Series values are
 *  coerced to numbers; the x value to a string category. A `splitBy` pivots a
 *  category column into one series per value. */
export function resultToDataset(
  result: QueryResultLike,
  viz: PanelVizSpec,
): ChartDataset | null {
  if (result.columns.length === 0) return null;
  const spec = { ...inferVizSpec(result), ...viz };
  const xKey = spec.xKey ?? result.columns[0]!;

  // Split-by: pivot the dimension column into series (long → wide).
  if (spec.splitBy && spec.valueKey && result.columns.includes(spec.splitBy)) {
    return pivotDataset(result, spec, xKey);
  }

  const seriesSpec: VizSeries[] =
    spec.series && spec.series.length > 0
      ? spec.series
      : [{ key: result.columns.find((c) => c !== xKey) ?? result.columns[0]! }];

  const palette = DEFAULT_PALETTE_ORDER.map(resolveToken);
  const series: DatasetSeries[] = seriesSpec.map((s) => ({
    key: s.key,
    name: s.name ?? s.key,
    ...(s.type ? { type: s.type } : {}),
  }));
  const colors = seriesSpec.map((s, i) =>
    s.color ? toColor(s.color, palette[i % palette.length]!) : palette[i % palette.length]!,
  );

  // Scatter/bubble need a numeric x; the category charts use a string category.
  const numericX = spec.chartType === "scatter" || spec.chartType === "bubble";
  const rows = result.rows.map((r) => {
    const xv = r[xKey];
    const row: Record<string, string | number | null> = {
      [xKey]: numericX ? (xv == null || xv === "" ? null : Number(xv)) : String(xv ?? ""),
    };
    for (const s of series) {
      const v = r[s.key];
      row[s.key] = v == null || v === "" ? null : Number(v);
    }
    return row;
  });

  return {
    key: "panel",
    label: "Preview",
    kind: spec.kind ?? "category",
    chartTypes: [
      "line",
      "area",
      "stacked-area",
      "bar",
      "horizontal-bar",
      "stacked-bar",
      "scatter",
      "bubble",
      "pie",
      "donut",
    ],
    categoryKey: xKey,
    rows,
    series,
    colors,
    valueFormat: makeValueFormatter(spec.valueFormat ?? "count"),
    ...(spec.trend ? { trend: spec.trend } : {}),
  };
}
